import React, {useEffect, useState} from 'react';
import {Alert, Box, Breadcrumbs, Button, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography} from '@mui/material';
import api from '../../api';
import {Link, useNavigate, useParams} from 'react-router-dom';
import DidNotLoad from "../../components/DidNotLoad";
import SalesChannelSelect from '../../components/checkout/SalesChannelSelect';
import PhoneInput from "../../components/PhoneInput";
import DocumentInput from "../../components/DocumentInput";
import ZipCodeInput from "../../components/ZipCodeInput";
import StateSelectInput from "../../components/StateSelectInput";
import {formatCurrencyInCents, formatDate, formatPhone, formatDocument, formatZipCode} from "../../helpers/formatters";
import { CUSTOMER_CATEGORY_CLIENTE, CUSTOMER_CATEGORY_OPTIONS, normalizeCustomerCategory } from '../../helpers/customers';

function EditCustomerPage() {
    const [name, setName] = useState('');
    const [category, setCategory] = useState(CUSTOMER_CATEGORY_CLIENTE);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [address, setAddress] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [addressComplement, setAddressComplement] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [originSalesChannelUuid, setOriginSalesChannelUuid] = useState('');
    const [originSalesChannelName, setOriginSalesChannelName] = useState('');
    const [salesStats, setSalesStats] = useState({
        orders_count: 0,
        orders_total_cents: 0,
        average_ticket_cents: 0,
        last_order_at: null,
        unpaid_orders_count: 0,
        unpaid_amount_cents: 0,
    });

    const { id } = useParams();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        document_number: null,
        name: null,
        category: null,
        origin_sales_channel_uuid: null,
        email: null,
        phone: null,
        address: null,
        address_number: null,
        address_complement: null,
        zip_code: null,
        city: null,
        state: null,
        neighborhood: null,
        has_errors: false
    });
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const handleOriginChannelChange = (channel) => {
        setOriginSalesChannelUuid(channel?.uuid || channel?.id || '');
        setOriginSalesChannelName(channel?.name || '');
    };

    useEffect(() => {
        api.get(`customers/${id}`)
            .then(response => {
                const customer = response.data.data;
                setName(customer.name);
                setCategory(normalizeCustomerCategory(customer.category));
                setPhone(formatPhone(customer.phone));
                setEmail(customer.email);
                setDocumentNumber(formatDocument(customer.document ?? ''));
                setAddress(customer.address ?? '');
                setAddressNumber(customer.address_number ?? '');
                setAddressComplement(customer.address_complement ?? '');
                setZipCode(formatZipCode(customer.zip_code ?? ''));
                setCity(customer.city ?? '');
                setState(customer.state ?? '');
                setNeighborhood(customer.neighborhood ?? '');
                setOriginSalesChannelUuid(customer.origin_sales_channel_uuid || '');
                setOriginSalesChannelName(customer.origin_sales_channel_name || '');
                setSalesStats({
                    orders_count: Number(customer.orders_count) || 0,
                    orders_total_cents: Number(customer.orders_total_cents) || 0,
                    average_ticket_cents: Number(customer.average_ticket_cents) || 0,
                    last_order_at: customer.last_order_at || null,
                    unpaid_orders_count: Number(customer.unpaid_orders_count) || 0,
                    unpaid_amount_cents: Number(customer.unpaid_amount_cents) || 0,
                });
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false
        if (name === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                name: 'O nome do cliente é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading
        let hasResponse = false;

        const newCustomer = {
            document: documentNumber.trim() === '' ? null : documentNumber,
            name: name,
            category,
            email: email,
            phone: phone.trim() === '' ? null : phone,
            address: address,
            address_number: addressNumber,
            address_complement: addressComplement,
            zip_code: zipCode,
            city: city,
            state: state,
            neighborhood: neighborhood,
            origin_sales_channel_uuid: originSalesChannelUuid,
            origin_sales_channel_name: originSalesChannelName,
        };

        api.put(`customers/${id}`, newCustomer)
            .then((response) => {
                hasResponse = true;
                console.log('Cliente editado com sucesso:', response);
                navigate('/admin/customers');  // Redireciona de volta para a lista de impressoras
            })
            .catch((error) => {
                hasResponse = true;
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false); // Desativa o estado de loading
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/customers')} to={'/admin/customers'}>
                    Clientes
                </Link>
                <Typography color="text.primary">Cadastro de cliente</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Cadastro de cliente</Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" display="block">Vendas</Typography>
                            <Typography variant="h6" fontWeight={700}>
                                {salesStats.orders_count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {salesStats.orders_count > 0
                                    ? formatCurrencyInCents(salesStats.orders_total_cents)
                                    : 'Sem histórico'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" display="block">Ticket médio</Typography>
                            <Typography variant="h6" fontWeight={700}>
                                {salesStats.orders_count > 0
                                    ? formatCurrencyInCents(salesStats.average_ticket_cents)
                                    : '—'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" display="block">Última compra</Typography>
                            <Typography variant="h6" fontWeight={700}>
                                {salesStats.last_order_at
                                    ? formatDate(salesStats.last_order_at)
                                    : '—'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card variant="outlined" sx={{
                        borderColor: salesStats.unpaid_amount_cents > 0 ? 'warning.main' : 'divider',
                    }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="caption" color="text.secondary" display="block">Em aberto</Typography>
                            <Typography variant="h6" fontWeight={700} color={salesStats.unpaid_amount_cents > 0 ? 'warning.main' : 'inherit'}>
                                {salesStats.unpaid_amount_cents > 0
                                    ? formatCurrencyInCents(salesStats.unpaid_amount_cents)
                                    : 'Em dia'}
                            </Typography>
                            {salesStats.unpaid_orders_count > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {salesStats.unpaid_orders_count} venda{salesStats.unpaid_orders_count === 1 ? '' : 's'}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {salesStats.unpaid_amount_cents > 0 && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => navigate('/admin/orders')}>
                            Ver vendas
                        </Button>
                    }
                >
                    Este cliente possui {formatCurrencyInCents(salesStats.unpaid_amount_cents)} em vendas não pagas.
                </Alert>
            )}

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Identificação</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="edit-customer-category-label">Categoria</InputLabel>
                            <Select
                                labelId="edit-customer-category-label"
                                label="Categoria"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {CUSTOMER_CATEGORY_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {resultErrors.category && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.category}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DocumentInput
                            documentNumber={documentNumber}
                            setDocumentNumber={setDocumentNumber}
                        />
                        {resultErrors.document && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.document}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SalesChannelSelect
                            label="Origem do cadastro"
                            value={originSalesChannelUuid}
                            onChange={(e) => setOriginSalesChannelUuid(e.target.value)}
                            onChannelChange={handleOriginChannelChange}
                            allowEmpty
                            emptyLabel="Sem origem"
                        />
                        {resultErrors.origin_sales_channel_uuid && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.origin_sales_channel_uuid}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr/></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Contatos</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PhoneInput
                            label="Telefone/Whats"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={false}
                        />
                        {resultErrors.phone && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.phone}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="E-mail"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {resultErrors.email && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.email}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr/></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Endereço</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ZipCodeInput
                            zipCode={zipCode}
                            setZipCode={setZipCode}
                            setCity={setCity}
                            setAddress={setAddress}
                            setState={setState}
                            setNeighborhood={setNeighborhood}
                        />
                        {resultErrors.zip_code && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.zip_code}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Endereço"
                            fullWidth
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                        />
                        {resultErrors.address && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.address}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Número"
                            fullWidth
                            value={addressNumber}
                            onChange={(e) => setAddressNumber(e.target.value)}
                            inputMode="numeric"
                        />
                        {resultErrors.address_number && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.address_number}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Complemento"
                            fullWidth
                            value={addressComplement}
                            onChange={(e) => setAddressComplement(e.target.value)}
                            inputMode="numeric"
                        />
                        {resultErrors.address_complement && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.address_complement}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Bairro"
                            fullWidth
                            value={neighborhood}
                            onChange={(e) => setNeighborhood(e.target.value)}
                        />
                        {resultErrors.neighborhood && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.neighborhood}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Cidade"
                            fullWidth
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                        {resultErrors.city && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.city}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <StateSelectInput
                            state={state}
                            setState={setState}
                        />
                        {resultErrors.state && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.state}
                            </div>
                        )}
                    </Grid>

                </Grid>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Empilha no mobile e mantém lado a lado no desktop
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 2,
                        marginBottom: 8,
                        gap: 2 // Adiciona espaçamento entre os itens no mobile
                    }}
                >
                    {/* Mensagem de erro à esquerda */}
                    {resultErrors.has_errors ? (
                        <div
                            className="text-danger"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            Ocorreram erros de validação, verifique os campos acima.
                        </div>
                    ) : (
                        <div style={{ height: '1rem' }} />
                    )}

                    {/* Botão responsivo */}
                    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                            startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                            sx={{ width: '100%' }} // Garante que o botão fique full-width no mobile
                        >
                            {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </Box>
                </Box>
            </form>
        </div>

    );
}

export default EditCustomerPage;
